import { useEffect, useMemo, useState } from 'react'
import * as stylex from '@stylexjs/stylex'

import Button from '../../components/Button'
import Card from '../../components/Card'
import { Field, FormMessage, Input, Textarea } from '../../components/FormControls'
import SettleUpLogo from '../../components/SettleUpLogo'
import {
    centsToInput,
    formatMoney,
    parseCents,
    personShareAPI,
    type GuestSettlementRequestPublic,
    type PersonShareView,
} from '../../lib/ledger'

const styles = stylex.create({
    page: { minHeight: '100vh', backgroundColor: '#f6f8f5', padding: '24px 16px 48px' },
    shell: { marginInline: 'auto', width: '100%', maxWidth: '720px' },
    logo: { marginBottom: '20px', transform: 'scale(.82)', transformOrigin: 'left center' },
    stack: { display: 'flex', flexDirection: 'column', gap: '16px' },
    header: { display: 'flex', flexDirection: 'column', gap: '5px' },
    eyebrow: { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: '#78907b' },
    title: { fontSize: '28px', fontWeight: 700, letterSpacing: '-.03em', color: '#26352a' },
    copy: { fontSize: '12px', lineHeight: '1.2rem', color: '#718075' },
    grid: { display: 'grid', gap: '10px', '@media (min-width: 580px)': { gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' } },
    balance: { borderRadius: '0.9rem', borderWidth: 1, borderColor: '#dce7d9', backgroundColor: '#f4f8f2', padding: '15px' },
    balanceAmount: { marginTop: '4px', fontSize: '25px', fontWeight: 700, color: '#315c3d' },
    stats: { marginTop: '10px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' },
    stat: { borderRadius: '0.65rem', backgroundColor: '#fff', padding: '9px' },
    statLabel: { display: 'block', fontSize: '10px', color: '#7a867c' },
    statValue: { display: 'block', marginTop: '2px', fontSize: '12px', fontWeight: 700, color: '#455248' },
    balanceActions: { marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' },
    section: { display: 'flex', flexDirection: 'column', gap: '10px' },
    sectionTitle: { fontSize: '14px', fontWeight: 700, color: '#344139' },
    settleForm: { display: 'flex', flexDirection: 'column', gap: '12px', borderRadius: '0.9rem', borderWidth: 1, borderColor: '#dce7d9', backgroundColor: '#fbfdfb', padding: '14px' },
    settleHeader: { display: 'flex', alignItems: 'start', justifyContent: 'space-between', gap: '12px' },
    settleTitle: { fontSize: '13px', fontWeight: 700, color: '#344139' },
    settleCopy: { marginTop: '3px', fontSize: '11px', lineHeight: '1rem', color: '#748077' },
    formActions: { display: 'flex', justifyContent: 'flex-end', gap: '8px' },
    list: { display: 'flex', flexDirection: 'column' },
    row: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '14px', borderTopWidth: 1, borderColor: '#e8ede6', paddingBlock: '11px' },
    rowMain: { minWidth: 0 },
    rowTitle: { display: 'block', fontSize: '12px', fontWeight: 650, color: '#39463d' },
    rowMeta: { display: 'block', marginTop: '3px', fontSize: '10px', color: '#818b83' },
    rowAmount: { flexShrink: 0, fontSize: '12px', fontWeight: 700, color: '#425548' },
    positive: { color: '#397a4b' },
    negative: { color: '#9a6040' },
    requestPending: { color: '#997522' },
    requestConfirmed: { color: '#397a4b' },
    requestRejected: { color: '#9a5739' },
    footer: { marginTop: '14px', textAlign: 'center', fontSize: '10px', color: '#879188' },
})

function inactiveMessage(status: PersonShareView['status']) {
    if (status === 'expired') return 'This debt overview link has expired. Ask the sender for a new link.'
    if (status === 'revoked') return 'This debt overview link has been disabled. Ask the sender for a new link.'
    return ''
}

function requestStatusLabel(request: GuestSettlementRequestPublic) {
    if (request.status === 'confirmed') return 'Approved'
    if (request.status === 'rejected') return 'Rejected'
    return 'Waiting for approval'
}

function requestStatusStyle(request: GuestSettlementRequestPublic) {
    if (request.status === 'confirmed') return styles.requestConfirmed
    if (request.status === 'rejected') return styles.requestRejected
    return styles.requestPending
}

export default function PersonSharePage({ token }: { token: string }) {
    const [view, setView] = useState<PersonShareView | null>(null)
    const [settleCurrency, setSettleCurrency] = useState('')
    const [amount, setAmount] = useState('')
    const [note, setNote] = useState('')
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState('')

    useEffect(() => {
        let active = true
        void personShareAPI.get(token)
            .then((result) => { if (active) setView(result) })
            .catch((reason) => { if (active) setError(reason instanceof Error ? reason.message : 'This share link could not be opened.') })
            .finally(() => { if (active) setLoading(false) })
        return () => { active = false }
    }, [token])

    const activity = useMemo(() => {
        if (!view) return []
        return [
            ...(view.expenses ?? []).map((expense) => ({ id: `expense:${expense.id}`, kind: 'Expense', title: expense.description, date: expense.occurredAt, currency: expense.currency, amount: expense.balanceImpactCents })),
            ...(view.payments ?? []).map((payment) => ({ id: `payment:${payment.id}`, kind: 'Payment', title: payment.direction === 'paid_owner' ? `Payment to ${view.ownerName}` : `Payment from ${view.ownerName}`, date: payment.paidAt, currency: payment.currency, amount: payment.direction === 'paid_owner' ? -payment.amountCents : payment.amountCents })),
        ].sort((left, right) => new Date(right.date).getTime() - new Date(left.date).getTime())
    }, [view])

    const selectedBalance = view?.balances?.find((balance) => balance.currency === settleCurrency)

    function openSettlement(currency: string, requestableAmountCents: number) {
        setSettleCurrency(currency)
        setAmount(centsToInput(requestableAmountCents))
        setNote('')
        setError('')
        setSuccess('')
    }

    async function requestSettlement() {
        if (!view || !selectedBalance || saving) return
        const amountCents = parseCents(amount)
        if (amountCents === null || amountCents <= 0) {
            setError('Enter the amount you paid.')
            return
        }
        if (amountCents > selectedBalance.requestableAmountCents) {
            setError(`You can request at most ${formatMoney(selectedBalance.requestableAmountCents, selectedBalance.currency)} right now.`)
            return
        }
        setSaving(true)
        setError('')
        setSuccess('')
        try {
            const updated = await personShareAPI.requestSettlement(token, selectedBalance.currency, amountCents, note.trim())
            setView(updated)
            setSettleCurrency('')
            setAmount('')
            setNote('')
            setSuccess(`${formatMoney(amountCents, selectedBalance.currency)} was sent to ${updated.ownerName} for approval. Your balance has not changed yet.`)
        } catch (reason) {
            setError(reason instanceof Error ? reason.message : 'Your settlement request could not be sent.')
        } finally {
            setSaving(false)
        }
    }

    return <main {...stylex.props(styles.page)}>
        <div {...stylex.props(styles.shell)}>
            <SettleUpLogo {...stylex.props(styles.logo)} />
            <Card padding="lg">
                <div {...stylex.props(styles.stack)}>
                    {loading && <FormMessage>Loading debt overview…</FormMessage>}
                    {!loading && error && !view && <FormMessage tone="error">{error}</FormMessage>}
                    {view && <>
                        <header {...stylex.props(styles.header)}>
                            <span {...stylex.props(styles.eyebrow)}>Shared debt overview</span>
                            <h1 {...stylex.props(styles.title)}>{view.personName}</h1>
                            <p {...stylex.props(styles.copy)}>Shared by {view.ownerName} · last updated {new Date(view.updatedAt).toLocaleString()}</p>
                        </header>

                        {error && <FormMessage tone="error">{error}</FormMessage>}
                        {success && <FormMessage tone="success">{success}</FormMessage>}
                        {view.status !== 'active' && <FormMessage>{inactiveMessage(view.status)}</FormMessage>}

                        {view.status === 'active' && <>
                            {view.showBalances && <section {...stylex.props(styles.section)}>
                                <h2 {...stylex.props(styles.sectionTitle)}>Current balance</h2>
                                {(view.balances ?? []).length === 0 ? <FormMessage tone="success">You are settled up right now.</FormMessage> : <div {...stylex.props(styles.grid)}>{view.balances?.map((balance) => <div {...stylex.props(styles.balance)} key={balance.currency}>
                                    <span {...stylex.props(styles.eyebrow)}>{balance.amountCents >= 0 ? `You owe ${view.ownerName}` : `${view.ownerName} owes you`}</span>
                                    <div {...stylex.props(styles.balanceAmount)}>{formatMoney(Math.abs(balance.amountCents), balance.currency)}</div>
                                    <div {...stylex.props(styles.stats)}>
                                        <div {...stylex.props(styles.stat)}><span {...stylex.props(styles.statLabel)}>In progress</span><strong {...stylex.props(styles.statValue)}>{formatMoney(balance.inProgressCents, balance.currency)}</strong></div>
                                        <div {...stylex.props(styles.stat)}><span {...stylex.props(styles.statLabel)}>After pending</span><strong {...stylex.props(styles.statValue)}>{formatMoney(Math.abs(balance.remainingAfterPendingCents), balance.currency)}</strong></div>
                                    </div>
                                    {balance.amountCents > 0 && balance.requestableAmountCents > 0 && <div {...stylex.props(styles.balanceActions)}>
                                        <Button onClick={() => openSettlement(balance.currency, balance.requestableAmountCents)} size="sm">Settle</Button>
                                        <span {...stylex.props(styles.copy)}>Sends a request for approval</span>
                                    </div>}
                                </div>)}</div>}
                            </section>}

                            {selectedBalance && <section {...stylex.props(styles.settleForm)}>
                                <div {...stylex.props(styles.settleHeader)}>
                                    <div>
                                        <h2 {...stylex.props(styles.settleTitle)}>Request settlement · {selectedBalance.currency}</h2>
                                        <p {...stylex.props(styles.settleCopy)}>This does not change the debt yet. {view.ownerName} must confirm that the payment was received.</p>
                                    </div>
                                    <Button disabled={saving} onClick={() => setSettleCurrency('')} size="sm" variant="ghost">Cancel</Button>
                                </div>
                                <Field label="Amount">
                                    <Input inputMode="decimal" onChange={(event) => setAmount(event.target.value)} value={amount} />
                                </Field>
                                <Field label="Note (optional)">
                                    <Textarea maxLength={240} onChange={(event) => setNote(event.target.value)} placeholder="e.g. Bank transfer sent today" value={note} />
                                </Field>
                                <div {...stylex.props(styles.formActions)}>
                                    <Button disabled={saving} onClick={() => void requestSettlement()}>{saving ? 'Sending request…' : 'Send settlement request'}</Button>
                                </div>
                            </section>}

                            {(view.requests ?? []).length > 0 && <section {...stylex.props(styles.section)}>
                                <h2 {...stylex.props(styles.sectionTitle)}>Settlement requests</h2>
                                <div {...stylex.props(styles.list)}>{view.requests?.map((request, index) => <div {...stylex.props(styles.row)} key={`${request.requestedAt}:${request.currency}:${index}`}>
                                    <span {...stylex.props(styles.rowMain)}>
                                        <strong {...stylex.props(styles.rowTitle)}>{formatMoney(request.amountCents, request.currency)}</strong>
                                        <span {...stylex.props(styles.rowMeta)}>{request.note ? `${request.note} · ` : ''}{new Date(request.requestedAt).toLocaleString()}</span>
                                        {request.rejectionReason && <span {...stylex.props(styles.rowMeta)}>Reason: {request.rejectionReason}</span>}
                                    </span>
                                    <strong {...stylex.props(styles.rowAmount, requestStatusStyle(request))}>{requestStatusLabel(request)}</strong>
                                </div>)}</div>
                            </section>}

                            {(view.loans ?? []).length > 0 && <section {...stylex.props(styles.section)}>
                                <h2 {...stylex.props(styles.sectionTitle)}>Loans</h2>
                                <div {...stylex.props(styles.list)}>{view.loans?.map((loan) => <div {...stylex.props(styles.row)} key={loan.id}><span {...stylex.props(styles.rowMain)}><strong {...stylex.props(styles.rowTitle)}>{loan.direction === 'person_borrowed' ? `Loan from ${view.ownerName}` : `Loan to ${view.ownerName}`}</strong><span {...stylex.props(styles.rowMeta)}>{loan.status}{loan.dueAt ? ` · due ${new Date(loan.dueAt).toLocaleDateString()}` : ''}</span></span><strong {...stylex.props(styles.rowAmount)}>{formatMoney(loan.outstandingCents, loan.currency)}</strong></div>)}</div>
                            </section>}

                            {activity.length > 0 && <section {...stylex.props(styles.section)}>
                                <h2 {...stylex.props(styles.sectionTitle)}>Activity</h2>
                                <div {...stylex.props(styles.list)}>{activity.slice(0, 100).map((item) => <div {...stylex.props(styles.row)} key={item.id}><span {...stylex.props(styles.rowMain)}><strong {...stylex.props(styles.rowTitle)}>{item.title}</strong><span {...stylex.props(styles.rowMeta)}>{item.kind} · {new Date(item.date).toLocaleDateString()}</span></span><strong {...stylex.props(styles.rowAmount, item.amount > 0 ? styles.positive : styles.negative)}>{item.amount > 0 ? '+' : item.amount < 0 ? '−' : ''}{formatMoney(Math.abs(item.amount), item.currency)}</strong></div>)}</div>
                            </section>}
                        </>}
                    </>}
                </div>
            </Card>
            <p {...stylex.props(styles.footer)}>Settlement requests stay pending until the sender approves them.</p>
        </div>
    </main>
}
